import { useState } from "react";

import { AgentVersionCard } from "src/agentNode/components/agentSelectionModal/AgentVersionCard";
import { Pill } from "src/base-components/Pill";
import { SearchBox } from "src/base-components/SearchBox";
import { AgentVersion, AgentVersionStatus } from "src/clients/agent-management";
import { useFuseSearch } from "src/utils/useFuseSearch";

type VersionStatusFilterValue = "all" | "published" | "draft";

const filterOptions: { label: string; value: VersionStatusFilterValue }[] = [
  { label: "All", value: "all" },
  { label: "Published", value: "published" },
  { label: "Draft", value: "draft" },
];

type FilterPillProps = {
  text: string;
  isActive: boolean;
  onClick: () => void;
};

const FilterPill: React.FC<FilterPillProps> = ({ isActive, text, onClick }) => (
  <Pill
    variant={isActive ? "indigo" : "outlined-white"}
    onClick={isActive ? undefined : onClick}
  >
    <Pill.Text>{text}</Pill.Text>
  </Pill>
);

type FilterProps = {
  value: VersionStatusFilterValue;
  onChange: (value: VersionStatusFilterValue) => void;
};

const AgentVersionFilter: React.FC<FilterProps> = ({ value, onChange }) => (
  <div className="flex gap-x-2">
    {filterOptions.map((option) => (
      <FilterPill
        key={option.value}
        isActive={value === option.value}
        text={option.label}
        onClick={() => onChange(option.value)}
      />
    ))}
  </div>
);

const matchesFilter = (
  version: AgentVersion,
  filter: VersionStatusFilterValue,
): boolean => {
  if (filter === "all") return true;
  if (filter === "published")
    return version.status === AgentVersionStatus.PUBLISHED;
  if (filter === "draft") return version.status === AgentVersionStatus.DRAFT;
  return true;
};

type Props = {
  versions: AgentVersion[];
  selectedVersionId?: string;
  onSelect: (version: AgentVersion) => void;
};

export const AgentVersionList: React.FC<Props> = ({
  versions,
  onSelect,
  selectedVersionId,
}) => {
  const [filter, setFilter] = useState<VersionStatusFilterValue>("all");
  const [filterTerm, setFilterTerm] = useState("");
  const search = useFuseSearch(
    versions.filter((version) => matchesFilter(version, filter)),
    { keys: ["name"] },
  );

  return (
    <div className="flex h-full flex-col gap-y-3">
      <div className="border-gray-100 border-b pb-2">
        <SearchBox placeholder="Search a version" onChange={setFilterTerm} />
      </div>
      <AgentVersionFilter value={filter} onChange={setFilter} />
      <ul className="decideScrollbar !overflow-y-scroll -mr-4 flex flex-col gap-y-3">
        {search(filterTerm)
          .sort(
            (a, b) =>
              +new Date(b.metadata.updated_at) -
              +new Date(a.metadata.updated_at),
          )
          .map((version) => (
            <AgentVersionCard
              key={version.id}
              selected={version.id === selectedVersionId}
              version={version}
              onClick={() => onSelect(version)}
            />
          ))}
      </ul>
    </div>
  );
};
